"use client";

import { useState, useCallback, useEffect } from "react";
import {
  CALCULATOR_CLEARANCE_DEFAULTS,
  CALCULATOR_CLEARANCE_HELPER_TEXT,
} from "@/lib/clearance/materialClearancePct";
import Link from "next/link";
import {
  Calculator,
  Ruler,
  Target,
  ArrowRight,
  Sparkles,
  FileText,
  AlertTriangle,
  Play,
} from "lucide-react";
import type { Locale } from "@/i18n/config";

interface Props {
  locale: Locale;
}

const MM_PER_INCH = 25.4;
const UNIT_KEY = "slitting-calc-unit";

type Unit = "mm" | "in";
type Material = "carbon" | "stainless" | "aluminium";

interface Pocket {
  stripWidth: number;
  spacerWidth: number;
  centerToCenter: number;
}

interface Results {
  totalStripWidth: number;
  totalKnifeWidth: number;
  totalSpacerWidth: number;
  scrapWidth: number;
  knifeCount: number;
  clearanceMm: number;
  clearancePct: number;
  usableWidth: number;
  remainingWidth: number;
  fits: boolean;
  coilWidthMm: number;
  edgeTrimMm: number;
  knifeTmm: number;
  expandedStrips: number[];
  pockets: Pocket[];
}

interface RawInputs {
  coilWidth: string;
  strips: { width: string; count: string }[];
  knifeThickness: string;
  materialThickness: string;
  clearancePercent: string;
  edgeTrim: string;
}

// Derived from the solver's own MATERIAL_CLEARANCE_PCT, not typed here. The
// hand written literals (carbon 10, stainless 12, aluminium 8) disagreed with
// the solver and with the prose lower down this same page.
const CLEARANCE_BY_MATERIAL: Record<Material, string> = CALCULATOR_CLEARANCE_DEFAULTS;

// All math runs in millimetres. `unit` only governs how the user types and reads values.
function computeResults(inp: RawInputs, unit: Unit): Results | null {
  const toMm = (s: string, fallbackMm: number) => {
    const n = parseFloat(s);
    if (isNaN(n)) return fallbackMm;
    return unit === "in" ? n * MM_PER_INCH : n;
  };

  const coilW = toMm(inp.coilWidth, 0);
  const knifeT = toMm(inp.knifeThickness, 10);
  const matT = toMm(inp.materialThickness, 1);
  const trim = toMm(inp.edgeTrim, 0);
  const clearPct = parseFloat(inp.clearancePercent) || 10;

  const expandedStrips: number[] = [];
  for (const strip of inp.strips) {
    const w = toMm(strip.width, 0);
    const c = parseInt(strip.count) || 0;
    for (let i = 0; i < c; i++) expandedStrips.push(w);
  }

  if (expandedStrips.length === 0 || coilW === 0) return null;

  const totalStripWidth = expandedStrips.reduce((a, b) => a + b, 0);
  const knifeCount = expandedStrips.length + 1;
  const totalKnifeWidth = knifeCount * knifeT;
  const clearanceMm = matT * (clearPct / 100);

  const pockets: Pocket[] = expandedStrips.map((stripWidth) => ({
    stripWidth,
    spacerWidth: stripWidth - knifeT,
    centerToCenter: stripWidth + knifeT,
  }));

  const totalSpacerWidth = pockets.reduce((a, p) => a + p.spacerWidth, 0);
  const usableWidth = coilW - 2 * trim;
  const remainingWidth = usableWidth - totalStripWidth;
  const scrapWidth = coilW - totalStripWidth;
  const fits = totalStripWidth <= usableWidth;

  return {
    totalStripWidth,
    totalKnifeWidth,
    totalSpacerWidth,
    scrapWidth,
    knifeCount,
    clearanceMm,
    clearancePct: clearPct,
    usableWidth,
    remainingWidth,
    fits,
    coilWidthMm: coilW,
    edgeTrimMm: trim,
    knifeTmm: knifeT,
    expandedStrips,
    pockets,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SCALE DIAGRAM — theme aware SVG drawn left to right across the coil width
// ─────────────────────────────────────────────────────────────────────────────
function SlitDiagram({ results, unit }: { results: Results; unit: Unit }) {
  const { coilWidthMm, edgeTrimMm, expandedStrips, totalStripWidth, remainingWidth } = results;
  const trim = edgeTrimMm;
  const uL = unit === "in" ? "in" : "mm";
  const fmt = (mm: number) => (unit === "in" ? (mm / MM_PER_INCH).toFixed(2) : Math.round(mm).toString());

  const totalSpan = Math.max(coilWidthMm, 2 * trim + totalStripWidth);
  const VB_W = 1000;
  const sc = (mm: number) => (mm / totalSpan) * VB_W;

  const barY = 28;
  const barH = 64;
  const barBottom = barY + barH;

  type Seg =
    | { kind: "trim"; x: number; w: number }
    | { kind: "strip"; x: number; w: number; mm: number; idx: number }
    | { kind: "rem"; x: number; w: number; mm: number };

  const segs: Seg[] = [];
  const knifeX: number[] = [];
  let x = 0;

  if (trim > 0) {
    segs.push({ kind: "trim", x, w: sc(trim) });
    x += sc(trim);
  }
  knifeX.push(x);
  expandedStrips.forEach((mm, i) => {
    const w = sc(mm);
    segs.push({ kind: "strip", x, w, mm, idx: i + 1 });
    x += w;
    knifeX.push(x);
  });
  const rem = remainingWidth > 0.0001 ? remainingWidth : 0;
  if (rem > 0) {
    segs.push({ kind: "rem", x, w: sc(rem), mm: rem });
    x += sc(rem);
  }
  if (trim > 0) {
    segs.push({ kind: "trim", x, w: sc(trim) });
    x += sc(trim);
  }

  const stripFill = (idx: number) => (idx % 2 === 1 ? "fill-[#d47723]" : "fill-[#b85f1a]");

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${VB_W} 132`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Slitting pattern drawn to scale across a ${fmt(coilWidthMm)} ${uL} coil`}
      >
        <defs>
          <pattern
            id="edgeTrimHatch"
            width="10"
            height="10"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="10" height="10" className="fill-slate-200 dark:fill-slate-700" />
            <line x1="0" y1="0" x2="0" y2="10" className="stroke-slate-400 dark:stroke-slate-500" strokeWidth="4" />
          </pattern>
        </defs>

        {/* Base plate */}
        <rect
          x="0"
          y={barY}
          width={VB_W}
          height={barH}
          rx="6"
          className="fill-slate-100 dark:fill-slate-900 stroke-slate-300 dark:stroke-slate-600"
          strokeWidth="2"
        />

        {/* Segments */}
        {segs.map((s, i) => {
          if (s.kind === "trim") {
            return (
              <g key={i}>
                <rect x={s.x} y={barY} width={s.w} height={barH} fill="url(#edgeTrimHatch)" />
              </g>
            );
          }
          if (s.kind === "rem") {
            return (
              <g key={i}>
                <rect
                  x={s.x}
                  y={barY}
                  width={s.w}
                  height={barH}
                  className="fill-amber-400/70 dark:fill-amber-500/40"
                />
                {s.w > 46 && (
                  <text
                    x={s.x + s.w / 2}
                    y={barY + barH / 2 + 7}
                    textAnchor="middle"
                    className="fill-amber-800 dark:fill-amber-200"
                    fontSize="20"
                    fontWeight="600"
                  >
                    {fmt(s.mm)}
                  </text>
                )}
              </g>
            );
          }
          return (
            <g key={i}>
              <rect x={s.x} y={barY} width={s.w} height={barH} className={stripFill(s.idx)} />
              {s.w > 44 && (
                <text
                  x={s.x + s.w / 2}
                  y={barY + barH / 2 + 8}
                  textAnchor="middle"
                  className="fill-white"
                  fontSize="24"
                  fontWeight="700"
                >
                  {fmt(s.mm)}
                </text>
              )}
            </g>
          );
        })}

        {/* Knife marks */}
        {knifeX.map((kx, i) => (
          <rect
            key={`k-${i}`}
            x={kx - 2}
            y={barY - 6}
            width="4"
            height={barH + 12}
            className="fill-slate-800 dark:fill-slate-100"
          />
        ))}

        {/* Scale ticks */}
        <text x="0" y={barBottom + 26} textAnchor="start" className="fill-slate-500 dark:fill-slate-400" fontSize="18">
          0
        </text>
        <text
          x={sc(coilWidthMm)}
          y={barBottom + 26}
          textAnchor={coilWidthMm >= totalSpan ? "end" : "middle"}
          className="fill-slate-500 dark:fill-slate-400"
          fontSize="18"
        >
          {fmt(coilWidthMm)} {uL}
        </text>
      </svg>

      {/* Legend (keeps every block readable on mobile regardless of bar scale) */}
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-600 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-[#d47723]" />
          Slit strip
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-1 h-3 rounded-sm bg-slate-800 dark:bg-slate-100" />
          Knife
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm border border-slate-400 dark:border-slate-500 bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,#94a3b8_2px,#94a3b8_3px)]" />
          Edge trim ({fmt(trim)} {uL}/side)
        </span>
        {rem > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-4 h-3 rounded-sm bg-amber-400/70" />
            Remaining ({fmt(rem)} {uL})
          </span>
        )}
      </div>
    </div>
  );
}

export default function SlittingCalculatorInteractive({ locale }: Props) {
  // Calculator state (input strings are held in the current display unit)
  const [unit, setUnit] = useState<Unit>("mm");
  const [coilWidth, setCoilWidth] = useState("1500");
  const [strips, setStrips] = useState<{ width: string; count: string }[]>([
    { width: "200", count: "3" },
    { width: "150", count: "2" },
  ]);
  const [knifeThickness, setKnifeThickness] = useState("10");
  const [materialThickness, setMaterialThickness] = useState("1.0");
  const [clearancePercent, setClearancePercent] = useState("10");
  const [edgeTrim, setEdgeTrim] = useState("10");
  const [material, setMaterial] = useState<Material>("carbon");
  const [calculated, setCalculated] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const [results, setResults] = useState<Results | null>(null);

  // Convert every length-valued field by a factor (counts and percent are left alone).
  const convertAll = useCallback((factor: number) => {
    const conv = (s: string) => {
      const n = parseFloat(s);
      if (isNaN(n)) return s;
      return parseFloat((n * factor).toFixed(4)).toString();
    };
    setCoilWidth((p) => conv(p));
    setKnifeThickness((p) => conv(p));
    setMaterialThickness((p) => conv(p));
    setEdgeTrim((p) => conv(p));
    setStrips((p) => p.map((s) => ({ ...s, width: conv(s.width) })));
  }, []);

  // Restore the saved unit on mount and convert the default mm values into it.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(UNIT_KEY);
      if (saved === "in") {
        convertAll(1 / MM_PER_INCH);
        setUnit("in");
      }
    } catch {
      // localStorage unavailable — stay on the mm default
    }
  }, [convertAll]);

  const handleUnitChange = useCallback(
    (next: Unit) => {
      if (next === unit) return;
      convertAll(next === "in" ? 1 / MM_PER_INCH : MM_PER_INCH);
      setUnit(next);
      try {
        localStorage.setItem(UNIT_KEY, next);
      } catch {
        // ignore persistence failure
      }
    },
    [unit, convertAll]
  );

  const addStrip = useCallback(() => {
    if (strips.length < 10) {
      setStrips([...strips, { width: unit === "in" ? "4" : "100", count: "1" }]);
    }
  }, [strips, unit]);

  const removeStrip = useCallback(
    (index: number) => {
      if (strips.length > 1) {
        setStrips(strips.filter((_, i) => i !== index));
      }
    },
    [strips]
  );

  const updateStrip = useCallback(
    (index: number, field: "width" | "count", value: string) => {
      const newStrips = [...strips];
      newStrips[index][field] = value;
      setStrips(newStrips);
      setCalculated(false);
    },
    [strips]
  );

  const handleMaterial = useCallback((m: Material) => {
    setMaterial(m);
    setClearancePercent(CLEARANCE_BY_MATERIAL[m]);
    setCalculated(false);
  }, []);

  const calculate = useCallback(() => {
    const res = computeResults(
      { coilWidth, strips, knifeThickness, materialThickness, clearancePercent, edgeTrim },
      unit
    );
    if (!res) return;
    setResults(res);
    if (calculated) {
      setTimeout(() => setShowEmailCapture(true), 1500);
    }
    setCalculated(true);
  }, [coilWidth, strips, knifeThickness, materialThickness, clearancePercent, edgeTrim, unit, calculated]);

  const loadExample = useCallback(() => {
    const exStrips = [
      { width: "100", count: "5" },
      { width: "75", count: "4" },
      { width: "80", count: "5" },
    ];
    setUnit("mm");
    try {
      localStorage.setItem(UNIT_KEY, "mm");
    } catch {
      // ignore
    }
    setCoilWidth("1250");
    setStrips(exStrips);
    setKnifeThickness("5");
    setMaterialThickness("1.0");
    setClearancePercent("10");
    setMaterial("carbon");
    setEdgeTrim("10");

    const res = computeResults(
      {
        coilWidth: "1250",
        strips: exStrips,
        knifeThickness: "5",
        materialThickness: "1.0",
        clearancePercent: "10",
        edgeTrim: "10",
      },
      "mm"
    );
    setResults(res);
    setCalculated(true);
  }, []);

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        await fetch("/api/capture-lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            source: "slitting-calculator-pdf",
            metadata: { coilWidth, strips, knifeThickness, unit },
          }),
        });
      } catch {
        // fire-and-forget — don't block the UX
      }
      setEmailSubmitted(true);
      setShowEmailCapture(false);
    },
    [email, coilWidth, strips, knifeThickness, unit]
  );

  // Display helpers — convert stored mm results into the active unit.
  const uL = unit === "in" ? "in" : "mm";
  const fmtLen = (mm: number, dec?: number) => {
    const v = unit === "in" ? mm / MM_PER_INCH : mm;
    return v.toFixed(dec ?? (unit === "in" ? 3 : 1));
  };
  const clearanceDecimals = unit === "in" ? 4 : 3;

  const inputClass =
    "w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#d47723] focus:border-transparent";

  return (
    <>
      <section className="py-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Credibility line */}
          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mb-6">
            Built by Maxwell Slitter Industries, making slitter tooling since 1976.
          </p>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Panel */}
            <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#d47723]/10">
                    <Ruler className="w-5 h-5 text-[#d47723]" />
                  </div>
                  <h2 className="text-xl font-semibold">Input Parameters</h2>
                </div>
                {/* Unit toggle */}
                <div
                  className="inline-flex rounded-lg border border-slate-300 dark:border-slate-600 p-0.5 bg-slate-100 dark:bg-slate-900/50"
                  role="group"
                  aria-label="Measurement unit"
                >
                  {(["mm", "in"] as Unit[]).map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => handleUnitChange(u)}
                      aria-pressed={unit === u}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                        unit === u
                          ? "bg-[#d47723] text-white shadow-sm"
                          : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                      }`}
                    >
                      {u === "mm" ? "mm" : "inch"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Load example + Material */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  onClick={loadExample}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 text-sm font-medium text-slate-700 dark:text-slate-200 hover:border-[#d47723]/50 hover:text-[#d47723] transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Load example
                </button>
                <div>
                  <select
                    value={material}
                    onChange={(e) => handleMaterial(e.target.value as Material)}
                    aria-label="Material"
                    className={`${inputClass} appearance-none cursor-pointer py-3`}
                  >
                    <option value="carbon">Carbon steel</option>
                    <option value="stainless">Stainless</option>
                    <option value="aluminium">Aluminium</option>
                  </select>
                </div>
              </div>

              {/* Coil Width */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Coil Width ({uL})
                </label>
                <input
                  type="number"
                  value={coilWidth}
                  onChange={(e) => {
                    setCoilWidth(e.target.value);
                    setCalculated(false);
                  }}
                  className={inputClass}
                  placeholder={unit === "in" ? "59" : "1500"}
                />
              </div>

              {/* Edge trim */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Edge trim (per side) ({uL})
                </label>
                <input
                  type="number"
                  step={unit === "in" ? "0.01" : "0.5"}
                  value={edgeTrim}
                  onChange={(e) => {
                    setEdgeTrim(e.target.value);
                    setCalculated(false);
                  }}
                  className={inputClass}
                  placeholder={unit === "in" ? "0.4" : "10"}
                />
                <p className="mt-2 text-xs text-slate-500">
                  Usable width is the coil width minus two times the edge trim. The slit pattern must fit inside it.
                </p>
              </div>

              {/* Strip Definitions */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Strip Widths ({uL})
                  </label>
                  <button
                    onClick={addStrip}
                    className="text-sm text-[#d47723] hover:text-[#c06a1f] transition-colors"
                  >
                    + Add Strip
                  </button>
                </div>
                <div className="space-y-3">
                  {strips.map((strip, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="number"
                          value={strip.width}
                          onChange={(e) => updateStrip(index, "width", e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#d47723] text-sm"
                          placeholder={`Width (${uL})`}
                        />
                      </div>
                      <div className="w-20">
                        <input
                          type="number"
                          value={strip.count}
                          onChange={(e) => updateStrip(index, "count", e.target.value)}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#d47723] text-sm text-center"
                          placeholder="Qty"
                          min="1"
                        />
                      </div>
                      {strips.length > 1 && (
                        <button
                          onClick={() => removeStrip(index)}
                          className="px-3 py-2.5 text-slate-400 hover:text-red-400 transition-colors"
                          aria-label={`Remove strip ${index + 1}`}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Knife & Material Parameters */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Knife Thickness ({uL})
                  </label>
                  <input
                    type="number"
                    step={unit === "in" ? "0.01" : "0.5"}
                    value={knifeThickness}
                    onChange={(e) => {
                      setKnifeThickness(e.target.value);
                      setCalculated(false);
                    }}
                    className={inputClass}
                    placeholder={unit === "in" ? "0.4" : "10"}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Material Gauge ({uL})
                  </label>
                  <input
                    type="number"
                    step={unit === "in" ? "0.001" : "0.1"}
                    value={materialThickness}
                    onChange={(e) => {
                      setMaterialThickness(e.target.value);
                      setCalculated(false);
                    }}
                    className={inputClass}
                    placeholder={unit === "in" ? "0.04" : "1.0"}
                  />
                </div>
              </div>

              {/* Clearance */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Knife Clearance (% of gauge)
                </label>
                <input
                  type="number"
                  value={clearancePercent}
                  onChange={(e) => {
                    setClearancePercent(e.target.value);
                    setCalculated(false);
                  }}
                  className={inputClass}
                  placeholder="10"
                />
                <p className="mt-2 text-xs text-slate-500">
                  Auto filled from material ({CALCULATOR_CLEARANCE_HELPER_TEXT}). Override anytime.
                </p>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculate}
                className="w-full py-4 bg-[#d47723] hover:bg-[#c06a1f] text-white rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#d47723]/25"
              >
                <Calculator className="w-5 h-5" />
                Calculate Setup
              </button>
            </div>

            {/* Results Panel */}
            <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Target className="w-5 h-5 text-green-500" />
                </div>
                <h2 className="text-xl font-semibold">Calculation Results</h2>
              </div>

              {!calculated ? (
                <div className="flex flex-col items-center justify-center h-80 text-slate-400 dark:text-slate-500">
                  <Calculator className="w-16 h-16 mb-4 opacity-30" />
                  <p>Enter parameters and click Calculate</p>
                </div>
              ) : results ? (
                <div className="space-y-6">
                  {/* Fit warning */}
                  {!results.fits && (
                    <div className="flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-700 dark:text-red-300">
                      <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-semibold">Pattern exceeds the usable width</p>
                        <p>
                          Your strips total {fmtLen(results.totalStripWidth)} {uL} but only{" "}
                          {fmtLen(results.usableWidth)} {uL} is usable after edge trim. Reduce strips or trim by{" "}
                          {fmtLen(Math.abs(results.remainingWidth))} {uL}.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Visual results diagram */}
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Setup Map (to scale)</h3>
                    <SlitDiagram results={results} unit={unit} />
                  </div>

                  {/* Headline metrics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-100 dark:bg-slate-900/50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Usable Width</p>
                      <p className="text-2xl font-bold text-slate-800 dark:text-white">
                        {fmtLen(results.usableWidth)} {uL}
                      </p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-900/50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Total Width Consumed</p>
                      <p className="text-2xl font-bold text-slate-800 dark:text-white">
                        {fmtLen(results.totalStripWidth)} {uL}
                      </p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-900/50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                        {results.remainingWidth >= 0 ? "Remaining Width" : "Over By"}
                      </p>
                      <p
                        className={`text-2xl font-bold ${
                          results.remainingWidth >= 0 ? "text-green-600 dark:text-green-400" : "text-red-500"
                        }`}
                      >
                        {fmtLen(Math.abs(results.remainingWidth))} {uL}
                      </p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-900/50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Total Scrap</p>
                      <p className="text-2xl font-bold text-amber-500">
                        {fmtLen(results.scrapWidth)} {uL}
                      </p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-900/50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Total Knives</p>
                      <p className="text-2xl font-bold text-slate-800 dark:text-white">{results.knifeCount}</p>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-900/50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Clearance</p>
                      <p className="text-2xl font-bold text-[#d47723]">
                        {fmtLen(results.clearanceMm, clearanceDecimals)} {uL}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{results.clearancePct}% of gauge</p>
                    </div>
                  </div>

                  {/* Pocket breakdown */}
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Pocket Breakdown</h3>
                    <div className="bg-slate-100 dark:bg-slate-900/50 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-slate-200 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-400 uppercase">
                        <div>Pocket</div>
                        <div>Strip</div>
                        <div>Spacer</div>
                        <div>Knife C-C</div>
                      </div>
                      <div className="divide-y divide-slate-200 dark:divide-slate-700/50 max-h-48 overflow-y-auto">
                        {results.pockets.map((pocket, i) => (
                          <div key={i} className="grid grid-cols-4 gap-2 px-4 py-3 text-sm">
                            <div className="text-slate-500 dark:text-slate-400">{i + 1}</div>
                            <div className="text-slate-800 dark:text-white">
                              {fmtLen(pocket.stripWidth)} {uL}
                            </div>
                            <div className="text-[#d47723]">
                              {fmtLen(pocket.spacerWidth)} {uL}
                            </div>
                            <div className="text-slate-600 dark:text-slate-300">
                              {fmtLen(pocket.centerToCenter)} {uL}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Total spacer width {fmtLen(results.totalSpacerWidth)} {uL} across {results.knifeCount} knives.
                    </p>
                  </div>

                  {/* Results adjacent CTA */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#d47723]/30 bg-[#d47723]/5 dark:bg-[#d47723]/10 p-4">
                    <p className="flex-1 text-sm text-slate-700 dark:text-slate-200">
                      Want this built from your actual inventory, with exact shims?
                    </p>
                    <Link
                      href="/login?mode=signup"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#d47723] hover:bg-[#c06a1f] text-white rounded-lg text-sm font-semibold whitespace-nowrap transition-colors"
                    >
                      Start OptiStack Pro free
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>

                  {/* CTA Box */}
                  <div className="bg-gradient-to-br from-[#d47723]/10 to-[#c06a1f]/10 dark:from-[#d47723]/20 dark:to-[#c06a1f]/20 rounded-xl p-6 border border-[#d47723]/20">
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-[#d47723]/20">
                        <Sparkles className="w-5 h-5 text-[#d47723]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800 dark:text-white mb-2">Want Production-Grade Accuracy?</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                          OptiStack Pro calculates exact shim combinations from your inventory,
                          generates printable assembly sheets, and achieves ±0.1mm precision.
                        </p>
                        <Link
                          href={`/${locale}/login?mode=signup`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#d47723] hover:bg-[#c06a1f] text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          Start 14-Day Free Trial
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Email Capture Modal */}
      {showEmailCapture && !emailSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 max-w-md w-full shadow-xl">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-[#d47723]/20 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-[#d47723]" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800 dark:text-white">Get Your Results as PDF</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Enter your email to download a detailed PDF with your calculation results and setup recommendations.
              </p>
            </div>
            <form onSubmit={handleEmailSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#d47723] mb-4"
              />
              <button
                type="submit"
                className="w-full py-3 bg-[#d47723] hover:bg-[#c06a1f] text-white rounded-lg font-semibold transition-all"
              >
                Send PDF
              </button>
            </form>
            <button
              onClick={() => setShowEmailCapture(false)}
              className="w-full mt-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
            >
              No thanks, continue without PDF
            </button>
          </div>
        </div>
      )}
    </>
  );
}
